import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { WarnLevels } from '../Constants/constants';
import _ from 'lodash';

import getStateManager from '../StateManager.js';
import { useSnapshot } from 'valtio';
import { createNestedSettingsDraft, createSettingsDraftStore } from '../Utils/settings_draft';

export function writeTempSettings(parent) {
  if (!_.isEmpty(parent.temp_settings)) {
    localStorage.setItem(`${parent.id}_temp_settings`, JSON.stringify(parent.temp_settings));
  } else {
    localStorage.removeItem(`${parent.id}_temp_settings`);
  }
}

/*
 * @param parent Can be either a controller or a zone if controller suppors multi zone
 */
const stateManager = getStateManager();
// Existing tabs use nested drafts and the normal StateManager save. Specialized
// forms can supply a draft adapter, editable baseline, and submission callback
// while sharing the controls lifecycle. onChange observes display changes only;
// persistence always goes through the draft adapter.
export function settingControls(controller, parent, tab_id, values, setValues, options = {}) {
  let currentValues = values;
  const baseline = () => (options.getBaseline ? options.getBaseline() : parent.settings[tab_id]);
  const draft =
    options.draft ??
    createNestedSettingsDraft(
      createSettingsDraftStore(parent, tab_id, () => writeTempSettings(parent))
    );
  const writeValues = (next, change) => {
    options.onChange?.(change);
    currentValues = next;
    setValues(next);
  };
  const canEdit = () => options.canEdit?.() ?? true;
  const entriesFor = (paths, nextValues, roundRoot = false) =>
    (Array.isArray(paths)
      ? paths.map((path, index) => [path, nextValues[index]])
      : [[paths, nextValues]]
    )
      .filter(([path]) => options.isEditablePath?.(path) ?? true)
      .map(([path, value]) => [
        path,
        options.normalizeValue
          ? options.normalizeValue(path, value)
          : roundRoot && path === null
            ? Math.round(value)
            : value
      ]);
  const controls = {
    saveTempSettings: (paths, nextValues) => {
      if (!canEdit()) return;
      const entries = entriesFor(paths, nextValues);
      if (entries.length) draft.edit(entries);
    },
    setValue: (paths, nextValues, saveTemp = true) => {
      if (!canEdit()) return;
      const entries = entriesFor(paths, nextValues, true);
      if (!entries.length) return;
      let next = _.cloneDeep(currentValues);
      entries.forEach(([path, value]) => {
        if (path === null) next = _.cloneDeep(value);
        else _.set(next, path, _.cloneDeep(value));
      });
      if (saveTemp) draft.edit(entries);
      writeValues(next, { type: 'edit', entries, saveTemp });
    },
    resetSetting: (path) => {
      const next = _.cloneDeep(currentValues);
      const value = _.cloneDeep(_.get(baseline(), path));
      _.set(next, path, value);
      draft.remove([path]);
      writeValues(next, { type: 'reset', entries: [[path, value]] });
    },
    resetSettings: () => {
      draft.clear();
      writeValues(_.cloneDeep(baseline()), { type: 'resetAll' });
    },
    restoreTempSettings: () => {
      writeValues(draft.restore(baseline()), { type: 'restore' });
    },
    hasChanged: (path) => !_.isEqual(_.get(currentValues, path), _.get(baseline(), path)),
    saveSettings: (transform = null, doTransform = false) => {
      if (options.submit) {
        if (!options.submit()) return false;
      } else {
        if (typeof transform !== 'function') transform = null;
        if (typeof doTransform !== 'boolean') doTransform = false;
        const sent = doTransform
          ? stateManager.saveSettings(controller, parent, transform(currentValues), tab_id, null)
          : stateManager.saveSettings(controller, parent, currentValues, tab_id, transform);
        if (!sent) return false;
      }
      draft.clear();
      return true;
    },
    addDeletedArrayIndex: (index, path = '') => {
      const deleted = _.cloneDeep(draft.read()._deleted_indicies ?? {});
      (deleted[path] ??= []).push(index);
      controls.saveTempSettings('_deleted_indicies', deleted);
    },
    setValueMinMaxSlider: (minPath, maxPath, min, max, saveTemp = true) =>
      controls.setValue([minPath, maxPath], [min, max], saveTemp),
    resetMinMaxSlider: (minPath, maxPath) => {
      const entries = [minPath, maxPath].map((path) => [
        path,
        _.cloneDeep(_.get(baseline(), path))
      ]);
      const next = _.cloneDeep(currentValues);
      entries.forEach(([path, value]) => _.set(next, path, value));
      draft.remove([minPath, maxPath]);
      writeValues(next, { type: 'reset', entries });
    }
  };
  return controls;
}

const unchangedSettings = (values) => values;

export const useTabSettings = (
  tab_id = null,
  exclude_from_effect = [],
  normalizeSettings = unchangedSettings
) => {
  let { t } = useTranslation();
  let params = useParams();
  let stateManager = getStateManager();
  const { controller, parent } = stateManager.getControllerForTab(params);
  let settings = useSnapshot(parent.settings);
  let t_id = tab_id ?? params.tab_id;
  const [storedValues, setStoredValues] = useState(() =>
    normalizeSettings(_.cloneDeep(settings[t_id]))
  );
  // Normalize both reads and writes so restored drafts and retained form state
  // cannot reintroduce fields from an incompatible settings format.
  const values = normalizeSettings(storedValues);
  const setValues = useCallback(
    (next) => {
      setStoredValues((previous) =>
        normalizeSettings(typeof next === 'function' ? next(normalizeSettings(previous)) : next)
      );
    },
    [normalizeSettings]
  );
  const controls = settingControls(controller, parent, t_id, values, setValues);

  // update state if someone else in another tab/browser/device make changes
  // for some reason this reacts pretty slow
  useEffect(() => {
    if (exclude_from_effect !== 'all') {
      // _.merge prioritizes the second parameter
      setValues(
        _.merge(_.cloneDeep(values), _.omit(_.cloneDeep(settings[t_id]), exclude_from_effect))
      );
    }
  }, [settings[t_id]]);

  // Restore after the initial baseline effect so automatic draft loading wins.
  const { openLoadSettingsDialog, closeLoadSettingsDialog } = useSavedSettings(parent, controls);

  return {
    t: t,
    params: params,
    stateManager: stateManager,
    controller: controller,
    settings: settings,
    parent: parent,
    values: values,
    setValues: setValues,
    controls: controls,
    openLoadSettingsDialog: openLoadSettingsDialog,
    closeLoadSettingsDialog: closeLoadSettingsDialog,
    tab_id: t_id
  };
};

export const useSavedSettings = (parent, controls, sid = null, custom_differ = null) => {
  let { t } = useTranslation();
  let { enqueueSnackbar } = useSnackbar();
  let { tab_id } = useParams();
  tab_id = sid ?? tab_id;
  const [open_load_settings_dialog, setOpenLoadSettingsDialog] = useState(false);
  const [settings_loaded, setSettingsLoaded] = useState(false);

  const differ = custom_differ
    ? custom_differ
    : () => {
        return (
          !_.isEmpty(parent.temp_settings[tab_id]) &&
          !_.isEqual(parent.settings[tab_id], parent.temp_settings[tab_id])
        );
      };

  useEffect(() => {
    // Get whether saved settings differ from controller settings.
    const differing = differ();

    const auto_load = localStorage.getItem('auto_load');
    setOpenLoadSettingsDialog(differing && !auto_load);
    // Check whether settings should be loaded (not yet loaded and differing)
    if (!settings_loaded && differing) {
      // Auto apply saved settings.
      if (auto_load === '1') {
        controls.restoreTempSettings();
        enqueueSnackbar(t('auto_saved_loaded'), { variant: 'info' });
        setSettingsLoaded(true);
      }
      // Auto delete saved settings.
      else if (auto_load === '0') {
        controls.resetSettings();
        enqueueSnackbar(t('auto_saved_deleted'), { variant: 'info' });
      }
    }
    // Warning when leaving page although unsaved settings exist.
    return () => {
      if (differ()) {
        enqueueSnackbar(t('auto_save_leaving_page_warn') + ' ' + t(tab_id), {
          variant: WarnLevels['unsaved_settings']
        });
      }
    };
  }, []);

  return {
    openLoadSettingsDialog: open_load_settings_dialog,
    closeLoadSettingsDialog: () => {
      setOpenLoadSettingsDialog(false);
    }
  };
};
